import { EntityType } from '@repo/common/enums/entityType';
import { isNil } from 'lodash-es';
import { type ReactNode } from 'react';
import type {
	ApiCatalogSort,
	IApiListResponse,
	ISecodaEntity,
	Namespace,
} from '../../api';
import { queryClient, searchQueryKeyFactory } from '../../api';
import { apiClient, getEndpoints } from '../../api/common';
import { SecodaEntityIcon } from '../../components/SecodaEntity';
import { search } from '../../lib/models';
import type { EditItem } from './types';
import {
	AutomationEditOption,
	AutomationEditType,
	EditDropdownType,
} from './types';

export function buildGetItemsById<T = ISecodaEntity>(
	namespace: Namespace,
	getIcon?: (entity: T) => ReactNode
) {
	return async (ids: string[]): Promise<EditItem[]> => {
		const promises = ids.map(async (id) => {
			try {
				const url = getEndpoints(namespace).byId(id);
				const entity = await apiClient.get(url).then((data) => data.data);
				const icon: ReactNode = getIcon ? (
					getIcon(entity)
				) : (
					<SecodaEntityIcon entity={entity} size={20} />
				);

				return {
					label: entity.title ?? entity.name,
					value: entity.id,
					icon,
				};
			} catch {
				// ignore errors - most likely due to a deleted entity
			}

			return null;
		});

		return (await Promise.all(promises)).filter(Boolean) as EditItem[];
	};
}

export async function buildGetItemsByIdFromSearch(ids: string[]) {
	const searchParams = {
		sort: { field: '_score', order: 'desc' } as ApiCatalogSort,
		filters: {
			operands: [],
			operator: 'in',
			field: 'id',
			value: ids,
		},
		page: 1,
	};

	const { results } = await queryClient.fetchQuery<
		IApiListResponse<ISecodaEntity>
	>({
		queryKey: searchQueryKeyFactory.list(1, searchParams),
		queryFn: async () => {
			const { data } = await search(searchParams);
			return data;
		},
	});

	return (
		results?.map((entity) => {
			let entityId = entity.id;

			if (
				entity.entity_type === EntityType.user &&
				entity.display_metadata.owners?.[0]?.id
			) {
				entityId = entity.display_metadata.owners?.[0]?.id;
			}

			return {
				label: entity.title || 'Untitled',
				value: entityId,
				metadata: entity,
				icon: (
					<SecodaEntityIcon entity={{ ...entity, id: entityId }} size={20} />
				),
			};
		}) ?? []
	);
}

export const getEditOption = (
	type: AutomationEditType
): AutomationEditOption => {
	switch (type) {
		case AutomationEditType.OWNERS_ADD:
		case AutomationEditType.OWNERS_REMOVE:
		case AutomationEditType.OWNERS_REPLACE:
		case AutomationEditType.OWNERS_CLEAR:
			return AutomationEditOption.OWNER;
		case AutomationEditType.SUBSCRIBERS_ADD:
		case AutomationEditType.SUBSCRIBERS_REMOVE:
		case AutomationEditType.SUBSCRIBERS_REPLACE:
		case AutomationEditType.SUBSCRIBERS_CLEAR:
			return AutomationEditOption.SUBSCRIBER;
		case AutomationEditType.TAGS_ADD:
		case AutomationEditType.TAGS_REMOVE:
		case AutomationEditType.TAGS_REPLACE:
		case AutomationEditType.TAGS_CLEAR:
			return AutomationEditOption.TAG;
		case AutomationEditType.TEAMS_ADD:
		case AutomationEditType.TEAMS_REMOVE:
		case AutomationEditType.TEAMS_REPLACE:
		case AutomationEditType.TEAMS_CLEAR:
		case AutomationEditType.TEAMS_ADD_ALL:
			return AutomationEditOption.TEAM;
		case AutomationEditType.COLLECTIONS_ADD:
		case AutomationEditType.COLLECTIONS_REMOVE:
		case AutomationEditType.COLLECTIONS_REPLACE:
		case AutomationEditType.COLLECTIONS_CLEAR:
			return AutomationEditOption.COLLECTION;
		case AutomationEditType.DEFINITION_SET:
		case AutomationEditType.DEFINITION_CLEAR:
			return AutomationEditOption.DOCUMENTATION;
		case AutomationEditType.DESCRIPTION_SET:
		case AutomationEditType.DESCRIPTION_CLEAR:
			return AutomationEditOption.DESCRIPTION;
		case AutomationEditType.PII_TRUE:
		case AutomationEditType.PII_FALSE:
			return AutomationEditOption.PII;
		case AutomationEditType.VERIFIED_TRUE:
		case AutomationEditType.VERIFIED_FALSE:
			return AutomationEditOption.VERIFIED;
		case AutomationEditType.PUBLISHED_TRUE:
		case AutomationEditType.PUBLISHED_FALSE:
			return AutomationEditOption.PUBLISHED;
		default:
			throw new Error(`Invalid edit type: ${type}`);
	}
};

export const getDropdownType = (type: AutomationEditType): EditDropdownType => {
	switch (type) {
		case AutomationEditType.OWNERS_ADD:
		case AutomationEditType.OWNERS_REMOVE:
		case AutomationEditType.OWNERS_REPLACE:
		case AutomationEditType.OWNERS_CLEAR:
		case AutomationEditType.SUBSCRIBERS_ADD:
		case AutomationEditType.SUBSCRIBERS_REMOVE:
		case AutomationEditType.SUBSCRIBERS_REPLACE:
		case AutomationEditType.SUBSCRIBERS_CLEAR:
		case AutomationEditType.TAGS_ADD:
		case AutomationEditType.TAGS_REMOVE:
		case AutomationEditType.TAGS_REPLACE:
		case AutomationEditType.TAGS_CLEAR:
		case AutomationEditType.TEAMS_ADD:
		case AutomationEditType.TEAMS_REMOVE:
		case AutomationEditType.TEAMS_REPLACE:
		case AutomationEditType.TEAMS_CLEAR:
		case AutomationEditType.TEAMS_ADD_ALL:
		case AutomationEditType.COLLECTIONS_ADD:
		case AutomationEditType.COLLECTIONS_REMOVE:
		case AutomationEditType.COLLECTIONS_REPLACE:
		case AutomationEditType.COLLECTIONS_CLEAR:
		case AutomationEditType.PII_TRUE:
		case AutomationEditType.PII_FALSE:
		case AutomationEditType.VERIFIED_TRUE:
		case AutomationEditType.VERIFIED_FALSE:
		case AutomationEditType.PUBLISHED_TRUE:
		case AutomationEditType.PUBLISHED_FALSE:
			return EditDropdownType.LIST;
		case AutomationEditType.DEFINITION_SET:
		case AutomationEditType.DEFINITION_CLEAR:
		case AutomationEditType.DESCRIPTION_SET:
		case AutomationEditType.DESCRIPTION_CLEAR:
			return EditDropdownType.STRING;
		default:
			throw new Error(`Invalid edit type: ${type}`);
	}
};

export const getEditActionLabel = (type: AutomationEditType): string => {
	let label = '';
	switch (type) {
		case AutomationEditType.OWNERS_ADD:
			label = 'Add owners';
			break;
		case AutomationEditType.OWNERS_REMOVE:
			label = 'Remove owners';
			break;
		case AutomationEditType.OWNERS_REPLACE:
			label = 'Replace owners';
			break;
		case AutomationEditType.OWNERS_CLEAR:
			label = 'Remove all owners';
			break;
		case AutomationEditType.SUBSCRIBERS_ADD:
			label = 'Add subscribers';
			break;
		case AutomationEditType.SUBSCRIBERS_REMOVE:
			label = 'Remove subscribers';
			break;
		case AutomationEditType.SUBSCRIBERS_REPLACE:
			label = 'Replace subscribers';
			break;
		case AutomationEditType.SUBSCRIBERS_CLEAR:
			label = 'Remove all subscribers';
			break;
		case AutomationEditType.TAGS_ADD:
			label = 'Add tags';
			break;
		case AutomationEditType.TAGS_REMOVE:
			label = 'Remove tags';
			break;
		case AutomationEditType.TAGS_REPLACE:
			label = 'Replace tags';
			break;
		case AutomationEditType.TAGS_CLEAR:
			label = 'Remove all tags';
			break;
		case AutomationEditType.TEAMS_ADD:
			label = 'Add teams';
			break;
		case AutomationEditType.TEAMS_REMOVE:
			label = 'Remove teams';
			break;
		case AutomationEditType.TEAMS_REPLACE:
			label = 'Replace teams';
			break;
		case AutomationEditType.TEAMS_CLEAR:
			label = 'Remove all teams';
			break;
		case AutomationEditType.TEAMS_ADD_ALL:
			label = 'Add all teams';
			break;
		case AutomationEditType.COLLECTIONS_ADD:
			label = 'Add collections';
			break;
		case AutomationEditType.COLLECTIONS_REMOVE:
			label = 'Remove collections';
			break;
		case AutomationEditType.COLLECTIONS_REPLACE:
			label = 'Replace collections';
			break;
		case AutomationEditType.COLLECTIONS_CLEAR:
			label = 'Clear collections';
			break;
		case AutomationEditType.DEFINITION_SET:
			label = 'Set definition';
			break;
		case AutomationEditType.DEFINITION_CLEAR:
			label = 'Clear definition';
			break;
		case AutomationEditType.DESCRIPTION_SET:
			label = 'Set description';
			break;
		case AutomationEditType.DESCRIPTION_CLEAR:
			label = 'Clear description';
			break;
		case AutomationEditType.PII_TRUE:
			label = 'Mark as PII';
			break;
		case AutomationEditType.PII_FALSE:
			label = 'Mark as not PII';
			break;
		case AutomationEditType.VERIFIED_TRUE:
			label = 'Mark as verified';
			break;
		case AutomationEditType.VERIFIED_FALSE:
			label = 'Mark as not verified';
			break;
		case AutomationEditType.PUBLISHED_TRUE:
			label = 'Mark as published';
			break;
		case AutomationEditType.PUBLISHED_FALSE:
			label = 'Mark as draft';
			break;
		default:
			return label;
	}
	return label;
};

export const requiresEditActionValue = (type: AutomationEditType): boolean => {
	switch (type) {
		case AutomationEditType.OWNERS_CLEAR:
		case AutomationEditType.SUBSCRIBERS_CLEAR:
		case AutomationEditType.TAGS_CLEAR:
		case AutomationEditType.TEAMS_CLEAR:
		case AutomationEditType.TEAMS_ADD_ALL:
		case AutomationEditType.COLLECTIONS_CLEAR:
		case AutomationEditType.DEFINITION_CLEAR:
		case AutomationEditType.DESCRIPTION_CLEAR:
		case AutomationEditType.PII_TRUE:
		case AutomationEditType.PII_FALSE:
		case AutomationEditType.VERIFIED_TRUE:
		case AutomationEditType.VERIFIED_FALSE:
		case AutomationEditType.PUBLISHED_TRUE:
		case AutomationEditType.PUBLISHED_FALSE:
			return false;
		default:
			return true;
	}
};

export const getValueAsArray = (value: EditItem) => {
	if (!value || isNil(value.value)) {
		return [];
	}
	return Array.isArray(value.value) ? value.value : [value.value];
};

export const getEditTypeActionFromAiSettingType = (
	type: string
): AutomationEditType => {
	switch (type) {
		case 'description':
			return AutomationEditType.DESCRIPTION_SET;
		default:
			throw new Error(`Invalid AI setting type: ${type}`);
	}
};
